// Provisioner trade catalog annotated per tenant. Mirrors useTrades /
// useBusinessUnits: read via operations (un-prefixed routes), and on
// enable/disable invalidate each derived catalog cache so the page reflows.
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { Trade } from '@/types/scheduler';

export interface TradeCatalogItem {
  key: string;
  label: string;
  slug: string;
  enabled: boolean;
  tradeId: string | null;
}

/** The provisioner trade catalog, annotated with this tenant's active Trades. */
export function useTradeCatalog() {
  return useApiQuery<TradeCatalogItem[]>({
    queryKey: schedulerKeys.tradeCatalog(),
    queryFn: () => apiGet<TradeCatalogItem[]>(operationsClient, schedulerEndpoints.tradeCatalog()),
    showErrorToast: false,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.tradeCatalog() });
    void queryClient.invalidateQueries({ queryKey: [...schedulerKeys.all(), 'trades'] });
    void queryClient.invalidateQueries({ queryKey: [...schedulerKeys.all(), 'businessUnits'] });
  };
}

/** Payload for adding a catalog trade: the catalog key + a picked color/icon. */
export interface EnableTradeInput {
  key: string;
  color?: string;
  icon?: string | null;
}

/** Add a catalog trade (creates/reactivates the tenant's Trade with color/icon). */
export function useEnableTrade() {
  const invalidate = useInvalidate();
  return useApiMutation<Trade, EnableTradeInput>({
    mutationFn: ({ key, color, icon }) =>
      apiPost<Trade, { color?: string; icon?: string | null }>(
        operationsClient,
        schedulerEndpoints.tradeCatalogEnable(key),
        { color, icon },
      ),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

/** Disable a catalog trade (archives the tenant's Trade). */
export function useDisableTrade() {
  const invalidate = useInvalidate();
  return useApiMutation<{ ok: true }, string>({
    mutationFn: (key) =>
      apiPost<{ ok: true }>(operationsClient, schedulerEndpoints.tradeCatalogDisable(key)),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}
